import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LoadingFallback from './LoadingFallback';

export interface ImpactReport {
  cascade?: Record<string, number>;
  orphan?: Record<string, number>;
  blocking?: Record<string, number>;
  blocked_reason?: string | null;
}

interface Props {
  open: boolean;
  title: string;
  entityLabel: string;
  loading?: boolean;
  busy?: boolean;
  impact: ImpactReport | null;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const PRETTY: Record<string, string> = {
  users: 'users',
  students: 'students',
  venues: 'venues',
  meets: 'meets',
  meet_events: 'meet events',
  event_types: 'event types',
  invites: 'invites',
  api_keys: 'API keys',
  registrations: 'registrations',
  results: 'results',
  heats: 'heats',
  coach_links: 'coach links',
  parent_links: 'parent links',
  student_coach_links: 'student-coach links',
  student_parent_links: 'student-parent links',
  invites_invited_by: 'invites they sent',
  api_keys_created_by: 'API keys they created',
  registrations_registered_by: 'registrations they recorded',
  students_with_user_link: 'students with a link to this user',
};

function pretty(key: string): string {
  return PRETTY[key] ?? key.replace(/_/g, ' ');
}

function nonZeroEntries(rec?: Record<string, number>) {
  if (!rec) return [] as [string, number][];
  return Object.entries(rec).filter(([, n]) => (n ?? 0) > 0) as [string, number][];
}

export default function ImpactDeleteDialog({
  open,
  title,
  entityLabel,
  loading,
  busy,
  impact,
  error,
  onCancel,
  onConfirm,
}: Props) {
  const cascade = nonZeroEntries(impact?.cascade);
  const orphan = nonZeroEntries(impact?.orphan);
  const blocking = nonZeroEntries(impact?.blocking);
  const blocked = !!impact?.blocked_reason || blocking.length > 0;
  const totalCascade = cascade.reduce((sum, [, n]) => sum + n, 0);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      aria-labelledby="impact-delete-title"
      aria-describedby="impact-delete-description"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="impact-delete-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" aria-hidden />
        {title}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LoadingFallback height={120} />}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && impact && (
          <Stack spacing={2} id="impact-delete-description">
            <Typography variant="body2">
              You're about to permanently delete this {entityLabel.toLowerCase()}.
              {totalCascade > 0
                ? ' The following data will be deleted along with it:'
                : orphan.length > 0
                  ? ' Nothing will be cascade-deleted, but the following links will be cleared:'
                  : ' No related data will be affected.'}
            </Typography>

            {cascade.length > 0 && (
              <Box>
                <Typography variant="overline" color="error.main">
                  Will be deleted
                </Typography>
                <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mt: 0.5 }}>
                  {cascade.map(([k, n]) => (
                    <Chip
                      key={k}
                      color="error"
                      variant="outlined"
                      label={`${n} ${pretty(k)}`}
                      aria-label={`${n} ${pretty(k)} will be deleted`}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {orphan.length > 0 && (
              <Box>
                <Typography variant="overline" color="warning.main">
                  Links will be cleared (rows kept)
                </Typography>
                <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mt: 0.5 }}>
                  {orphan.map(([k, n]) => (
                    <Chip
                      key={k}
                      color="warning"
                      variant="outlined"
                      label={`${n} ${pretty(k)}`}
                      aria-label={`${n} ${pretty(k)} will be unlinked`}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {blocking.length > 0 && (
              <Alert severity="error">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Cannot delete — there are dependent records:
                </Typography>
                <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mt: 1 }}>
                  {blocking.map(([k, n]) => (
                    <Chip key={k} color="error" size="small" label={`${n} ${pretty(k)}`} />
                  ))}
                </Stack>
              </Alert>
            )}

            {impact.blocked_reason && (
              <Alert severity="error">{impact.blocked_reason}</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy} aria-label="Cancel deletion">
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={busy || blocked || loading}
          aria-label={`Confirm delete ${entityLabel.toLowerCase()}`}
        >
          {busy ? 'Deleting…' : `Delete ${entityLabel.toLowerCase()}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
